import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import "../../styles/tiptap.css";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import ListItem from "@tiptap/extension-list-item";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import Heading from "@tiptap/extension-heading";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import Image from "@tiptap/extension-image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ImageLibraryModal from "./ImageLibraryModal";

const SectionTipTapEditor = ({
  section,
  content,
  onChange,
  onSave,
  autoSaveStatus,
  hasUnsavedChanges
}) => {
  const [showImageLibrary, setShowImageLibrary] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Heading.configure({
        levels: [1, 2, 3, 4, 5, 6],
      }),
      BulletList.configure({
        HTMLAttributes: {
          class: 'tiptap-bullet-list',
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: 'tiptap-ordered-list',
        },
      }),
      ListItem,
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'tiptap-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose-sm lg:prose mx-auto focus:outline-none max-w-none',
      },
    },
  });

  // Update editor content when prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, false);
    }
  }, [editor, content]);

  const handleInsertImage = (imageUrl, altText) => {
    if (editor) {
      editor.chain().focus().setImage({ src: imageUrl, alt: altText }).run();
    }
  };

  const MenuBar = () => {
    if (!editor) {
      return null;
    }

    return (
      <div className="border-b border-gray-200 p-2 bg-gray-50 flex flex-wrap items-center gap-1">
        {/* Headings */}
        <div className="flex items-center border-r border-gray-300 pr-2 mr-2">
          <select
            value={
              editor.isActive('heading', { level: 1 }) ? 'h1' :
              editor.isActive('heading', { level: 2 }) ? 'h2' :
              editor.isActive('heading', { level: 3 }) ? 'h3' :
              editor.isActive('heading', { level: 4 }) ? 'h4' :
              editor.isActive('paragraph') ? 'p' : 'p'
            }
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'p') {
                editor.chain().focus().setParagraph().run();
              } else {
                const level = parseInt(value.replace('h', ''));
                editor.chain().focus().toggleHeading({ level }).run();
              }
            }}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value="p">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
          </select>
        </div>

        {/* Text Formatting */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={`p-2 rounded text-sm ${
              editor.isActive('bold')
                ? 'bg-primary-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border border-gray-300`}
          >
            <FontAwesomeIcon icon="bold" />
          </button>

          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            className={`p-2 rounded text-sm ${
              editor.isActive('italic')
                ? 'bg-primary-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border border-gray-300`}
          >
            <FontAwesomeIcon icon="italic" />
          </button>

          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
            className={`p-2 rounded text-sm ${
              editor.isActive('strike')
                ? 'bg-primary-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border border-gray-300`}
          >
            <FontAwesomeIcon icon="strikethrough" />
          </button>
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded text-sm ${
              editor.isActive('bulletList')
                ? 'bg-primary-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border border-gray-300`}
          >
            <FontAwesomeIcon icon="list-ul" />
          </button>

          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-2 rounded text-sm ${
              editor.isActive('orderedList')
                ? 'bg-primary-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border border-gray-300`}
          >
            <FontAwesomeIcon icon="list-ol" />
          </button>
        </div>

        {/* Alignment */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`p-2 rounded text-sm ${
              editor.isActive({ textAlign: 'left' })
                ? 'bg-primary-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border border-gray-300`}
          >
            <FontAwesomeIcon icon="align-left" />
          </button>

          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`p-2 rounded text-sm ${
              editor.isActive({ textAlign: 'center' })
                ? 'bg-primary-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border border-gray-300`}
          >
            <FontAwesomeIcon icon="align-center" />
          </button>

          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`p-2 rounded text-sm ${
              editor.isActive({ textAlign: 'right' })
                ? 'bg-primary-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border border-gray-300`}
          >
            <FontAwesomeIcon icon="align-right" />
          </button>

          <button
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={`p-2 rounded text-sm ${
              editor.isActive({ textAlign: 'justify' })
                ? 'bg-primary-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border border-gray-300`}
          >
            <FontAwesomeIcon icon="align-justify" />
          </button>
        </div>

        {/* Text Highlighting */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <button
            onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()}
            className={`p-2 rounded text-sm border border-gray-300 ${
              editor.isActive('highlight', { color: '#fef08a' })
                ? 'ring-2 ring-yellow-500'
                : ''
            }`}
            style={{ backgroundColor: '#fef08a' }}
            title="Highlight Yellow"
          >
            <FontAwesomeIcon icon="highlighter" className="text-gray-700" />
          </button>

          <button
            onClick={() => editor.chain().focus().toggleHighlight({ color: '#bbf7d0' }).run()}
            className={`p-2 rounded text-sm border border-gray-300 ${
              editor.isActive('highlight', { color: '#bbf7d0' })
                ? 'ring-2 ring-green-500'
                : ''
            }`}
            style={{ backgroundColor: '#bbf7d0' }}
            title="Highlight Green"
          >
            <FontAwesomeIcon icon="highlighter" className="text-gray-700" />
          </button>

          <button
            onClick={() => editor.chain().focus().toggleHighlight({ color: '#bfdbfe' }).run()}
            className={`p-2 rounded text-sm border border-gray-300 ${
              editor.isActive('highlight', { color: '#bfdbfe' })
                ? 'ring-2 ring-blue-500'
                : ''
            }`}
            style={{ backgroundColor: '#bfdbfe' }}
            title="Highlight Blue"
          >
            <FontAwesomeIcon icon="highlighter" className="text-gray-700" />
          </button>

          <button
            onClick={() => editor.chain().focus().toggleHighlight({ color: '#fed7aa' }).run()}
            className={`p-2 rounded text-sm border border-gray-300 ${
              editor.isActive('highlight', { color: '#fed7aa' })
                ? 'ring-2 ring-orange-500'
                : ''
            }`}
            style={{ backgroundColor: '#fed7aa' }}
            title="Highlight Orange"
          >
            <FontAwesomeIcon icon="highlighter" className="text-gray-700" />
          </button>

          <button
            onClick={() => editor.chain().focus().toggleHighlight({ color: '#fecaca' }).run()}
            className={`p-2 rounded text-sm border border-gray-300 ${
              editor.isActive('highlight', { color: '#fecaca' })
                ? 'ring-2 ring-red-500'
                : ''
            }`}
            style={{ backgroundColor: '#fecaca' }}
            title="Highlight Red"
          >
            <FontAwesomeIcon icon="highlighter" className="text-gray-700" />
          </button>

          <button
            onClick={() => editor.chain().focus().unsetHighlight().run()}
            className="p-2 rounded text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
            title="Remove Highlight"
          >
            <FontAwesomeIcon icon="eraser" />
          </button>
        </div>

        {/* Table Controls */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <button
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            className="p-2 rounded text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
            title="Insert Table"
          >
            <FontAwesomeIcon icon="table" />
          </button>
          {editor.isActive('table') && (
            <>
              <button
                onClick={() => editor.chain().focus().addColumnBefore().run()}
                className="p-2 rounded text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                title="Add Column Before"
              >
                <FontAwesomeIcon icon="plus" className="text-xs" />
              </button>
              <button
                onClick={() => editor.chain().focus().deleteColumn().run()}
                className="p-2 rounded text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                title="Delete Column"
              >
                <FontAwesomeIcon icon="minus" className="text-xs" />
              </button>
              <button
                onClick={() => editor.chain().focus().addRowBefore().run()}
                className="p-2 rounded text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                title="Add Row Before"
              >
                <FontAwesomeIcon icon="plus" className="text-xs" />
              </button>
              <button
                onClick={() => editor.chain().focus().deleteRow().run()}
                className="p-2 rounded text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                title="Delete Row"
              >
                <FontAwesomeIcon icon="minus" className="text-xs" />
              </button>
              <button
                onClick={() => editor.chain().focus().deleteTable().run()}
                className="p-2 rounded text-sm bg-red-50 text-red-600 hover:bg-red-100 border border-red-300"
                title="Delete Table"
              >
                <FontAwesomeIcon icon="trash" className="text-xs" />
              </button>
            </>
          )}
        </div>

        {/* Insert Image */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <button
            onClick={() => setShowImageLibrary(true)}
            className="p-2 rounded text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
            title="Insert Image"
          >
            <FontAwesomeIcon icon="image" />
          </button>
        </div>

        {/* History */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
            className="p-2 rounded text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon="undo" />
          </button>

          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
            className="p-2 rounded text-sm bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon="redo" />
          </button>
        </div>
      </div>
    );
  };

  const getWordCount = () => {
    if (!editor) return 0;
    const text = editor.getText();
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Editor Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Section Editor</h3>
            <p className="text-sm text-gray-600">{section.section_title}</p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Status indicators */}
            {autoSaveStatus && (
              <div className="flex items-center space-x-2 text-sm">
                <FontAwesomeIcon
                  icon={
                    autoSaveStatus.includes("failed") ? "exclamation-triangle" :
                    autoSaveStatus.includes("saving") ? "spinner" :
                    "check"
                  }
                  className={`${
                    autoSaveStatus.includes("failed") ? "text-red-500" :
                    autoSaveStatus.includes("saving") ? "text-blue-500 animate-spin" :
                    "text-green-500"
                  }`}
                />
                <span className={`${
                  autoSaveStatus.includes("failed") ? "text-red-600" :
                  autoSaveStatus.includes("saving") ? "text-blue-600" :
                  "text-green-600"
                }`}>
                  {autoSaveStatus}
                </span>
              </div>
            )}

            {hasUnsavedChanges && !autoSaveStatus && (
              <div className="flex items-center space-x-2 text-sm text-amber-600">
                <FontAwesomeIcon icon="exclamation-triangle" />
                <span>Unsaved changes</span>
              </div>
            )}

            {/* Save button */}
            <button
              onClick={onSave}
              className="px-3 py-1 bg-primary-500 text-white text-sm rounded hover:bg-primary-600 transition-colors"
            >
              <FontAwesomeIcon icon="save" className="mr-1" />
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <MenuBar />

      {/* Editor */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          <EditorContent
            editor={editor}
            className="min-h-[400px] focus-within:outline-none"
          />
        </div>
      </div>

      {/* Footer Stats */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <span>Words: {getWordCount()}</span>
            <span>Characters: {editor ? editor.getText().length : 0}</span>
          </div>
          <div className="flex items-center space-x-2">
            {section.is_ai_generated && (
              <div className="flex items-center space-x-1 text-blue-600">
                <FontAwesomeIcon icon="robot" />
                <span>AI Generated</span>
              </div>
            )}
            <span>
              Last updated: {new Date(section.updated_at).toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>

      {/* Image Library Modal */}
      <ImageLibraryModal
        isOpen={showImageLibrary}
        onClose={() => setShowImageLibrary(false)}
        onSelectImage={handleInsertImage}
      />
    </div>
  );
};

export default SectionTipTapEditor;